import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { TezosToolkit } from "@taquito/taquito";
import { BeaconWallet } from "@taquito/beacon-wallet";

const Tezos = new TezosToolkit("https://mainnet.smartpy.io");
const wallet = new BeaconWallet({ name: "Temple Wallet Profile" });
Tezos.setWalletProvider(wallet);

const GHOSTNETtzktendpoint = process.env.REACT_APP_TZKT_GHOSTNET_API_ENDPOINT;
const generateAvatarurl = process.env.REACT_APP_GENERATE_AVATAR_URL;
const domainpath = process.env.REACT_APP_API_DOMAIN_ENDPOINT;
const ITEMS_PER_PAGE = 18;

const Profile = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [walletAddress, setWalletAddress] = useState("");
  const [activeTab, setActiveTab] = useState("created");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [profileImage, setProfileImage] = useState(null);
  const [error, setError] = useState("");

  // Generate avatar image
  const generateAvatar = (address) => {
    setProfileImage(`${generateAvatarurl}/${address}.png?size=200x200`);
  };

  // Fetch profile data from API
  const fetchProfileData = async (address, page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${domainpath}/api/user/profile`, {
        params: { address, page },
      });

      const profile = response.data;

      // Filters
      const offers = profile.all?.filter(
        (nft) =>
          nft.blockchain_metadata?.open_to_offers &&
          !nft.blockchain_metadata?.open_to_auction
      );

      const auctions = profile.all?.filter(
        (nft) =>
          nft.blockchain_metadata?.open_to_auction ||
          nft.auctionDetails?.isActive
      );

      setProfileData({
        ...profile,
        offers,
        auctions,
      });
      if (profile.profileImage) {
        setProfileImage(profile.profileImage);
      } else {
        generateAvatar(address);
      }

      setTotalPages(Math.ceil(profile.owned.length / ITEMS_PER_PAGE));
      setError("");
    } catch (err) {
      console.error("Error fetching profile data:", err);
      setError("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  // Load wallet address and fetch data on component mount
  useEffect(() => {
    const storedAddress = localStorage.getItem("walletAddress");
    if (storedAddress) {
      setWalletAddress(storedAddress);
      fetchProfileData(storedAddress);
    }
  }, []);

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage !== page && !loading) {
      setPage(newPage);
      fetchProfileData(walletAddress, newPage);
    }
  };

  // Format Tezos balance
  const formatBalance = (balance) => {
    return balance ? `${(balance / 1000000).toFixed(2)} ꜩ` : "0 ꜩ";
  };

  // Format date
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Render media based on mime_type
  const renderMedia = (nft) => {
    const ipfsUrl =
      nft.ipfsUrl?.replace("ipfs://", "https://ipfs.io/ipfs/") ||
      nft.blockchain_metadata?.ipfs_url?.replace(
        "ipfs://",
        "https://ipfs.io/ipfs/"
      );
    const mimeType = nft.blockchain_metadata?.mime_type;

    if (!ipfsUrl) {
      return (
        <img
          src="https://via.placeholder.com/100"
          alt={nft.title || "NFT"}
          className="w-full h-32 object-cover rounded"
        />
      );
    }

    switch (true) {
      case mimeType?.startsWith("image/gif"):
        return (
          <img
            src={ipfsUrl}
            alt={nft.title || "GIF NFT"}
            className="w-full h-32 object-cover rounded"
          />
        );

      case mimeType?.startsWith("image/"):
        return (
          <img
            src={ipfsUrl}
            alt={nft.title || "Image NFT"}
            className="w-full h-32 object-cover rounded"
          />
        );

      case mimeType?.startsWith("video/"):
        return (
          <video
            controls
            className="w-full h-32 object-cover rounded"
            poster={nft.thumbnailUrl || "https://via.placeholder.com/100"}
          >
            <source src={ipfsUrl} type={mimeType} />
            Your browser does not support the video tag.
          </video>
        );

      case mimeType?.startsWith("audio/"):
        return (
          <div className="w-full h-32 flex items-center justify-center bg-gray-100 rounded">
            <audio controls className="w-full">
              <source src={ipfsUrl} type={mimeType} />
              Your browser does not support the audio element.
            </audio>
          </div>
        );

      default:
        return (
          <img
            src={ipfsUrl}
            alt={nft.title || "NFT"}
            className="w-full h-32 object-cover rounded"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://via.placeholder.com/100";
            }}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-28">
      <div className="bg-white shadow-lg rounded-lg p-6 w-full">
        {/* Profile Section */}
        {profileData && (
          <div className="flex items-center space-x-4 p-4 border-b">
            <img
              src={profileImage || "https://via.placeholder.com/150"}
              alt="Profile Avatar"
              className="w-16 h-16 rounded-full border-2 border-gray-300"
            />
            <div>
              <h3 className="text-xl font-semibold">
                {profileData.profile.alias || profileData?.username}
              </h3>
              <p className="text-gray-600">{walletAddress}</p>
              <p className="text-gray-600">
                Balance: {formatBalance(profileData.profile.balance)}
              </p>
              <div className="flex space-x-4 mt-1">
                <span className="text-sm">
                  {profileData.followers} Followers
                </span>
                <span className="text-sm">
                  {profileData.following} Following
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Tabs Section */}
        <div className="flex space-x-4 mt-4 border-b pb-2 text-gray-600 overflow-x-auto">
          {["created", "owned", "collections", "activity"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-2 whitespace-nowrap ${
                activeTab === tab
                  ? "text-black font-bold border-b-2 border-black"
                  : ""
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="RoadmapSpinnerDiv" id="RoadmapSpinnerDiv">
            <span className="RoadmapSpinner"></span>
          </div>
        )}

        {/* Error State */}
        {error && <div className="p-4 text-red-500 text-center">{error}</div>}

        {/* Created NFTs */}
        {activeTab === "created" && profileData && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {profileData?.created.length > 0 ? (
              profileData.created.map((nft, index) => (
                <div
                  key={index}
                  onClick={() => navigate(`/nft/${nft.nft_id}`)}
                  className="p-3 border rounded-lg shadow-sm cursor-pointer hover:shadow-md transition"
                >
                  {renderMedia(nft)}
                  <p className="text-sm font-semibold mt-2 truncate">
                    {nft.title || "Untitled"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {nft.price ? `${nft.price} ꜩ` : "Not for sale"}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-500 col-span-full text-center py-8">
                No NFTs created yet
              </p>
            )}
          </div>
        )}

        {/* Owned NFTs */}
        {activeTab === "owned" && profileData && (
          <div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {profileData.owned
                .slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
                .map((nft, index) => (
                  <div
                    key={index}
                    onClick={() => navigate(`/nft/${nft.nft_id}`)}
                    className="p-3 border rounded-lg shadow-sm cursor-pointer hover:shadow-md transition"
                  >
                    {renderMedia(nft)}
                    <p className="text-sm font-semibold mt-2 truncate">
                      {nft.title || "Untitled"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {nft.price ? `${nft.price} ꜩ` : "Not for sale"}
                    </p>
                    {nft.currentBid && (
                      <p className="text-xs text-green-600">
                        Highest bid: {nft.currentBid} ꜩ
                      </p>
                    )}
                  </div>
                ))}
            </div>

            {/* Pagination */}
            {profileData.owned.length > ITEMS_PER_PAGE && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 mx-1 rounded bg-gray-200 disabled:opacity-50"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-4 py-2 mx-1 rounded ${
                        page === pageNum
                          ? "bg-blue-500 text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                )}
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="px-4 py-2 mx-1 rounded bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Collections */}
        {activeTab === "collections" && profileData && (
          <div className="mt-4">
            {profileData.collections.length > 0 ? (
              profileData.collections.map((collection, index) => (
                <div key={index} className="mb-6 border-b pb-4">
                  <h3 className="text-lg font-semibold mb-2">
                    {collection.description || `Collection ${index + 1}`}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {collection.included_nfts.map((nft, nftIndex) => (
                      <div
                        key={nftIndex}
                        onClick={() => navigate(`/nft/${nft.key}`)}
                        className="p-2 border rounded shadow-sm cursor-pointer hover:shadow-md transition"
                      >
                        {renderMedia({
                          ...nft,
                          ipfsUrl: nft.metadata.ipfs_url,
                          blockchain_metadata: nft.metadata,
                        })}
                        <p className="text-xs mt-1 truncate">
                          {nft.metadata.name || `NFT ${nftIndex}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">
                No collections found
              </p>
            )}
          </div>
        )}

        {/* Activity */}
        {activeTab === "activity" && profileData && (
          <div className="mt-4">
            {profileData.activity.length > 0 ? (
              <div className="space-y-4">
                {profileData.activity.map((tx, index) => (
                  <div
                    key={index}
                    className="p-3 border rounded-lg shadow-sm hover:shadow-md transition"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">
                          {tx.sender?.alias || tx.sender?.address || "Unknown"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {tx.parameters?.entrypoint || "Transaction"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">
                          {tx.amount ? `${tx.amount / 1000000} ꜩ` : "N/A"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(tx.timestamp)}
                        </p>
                      </div>
                    </div>
                    {tx.parameters && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                        <pre className="whitespace-pre-wrap">
                          {JSON.stringify(tx.parameters.value, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No activity found
              </p>
            )}
          </div>
        )}

        {/* Auctions */}
        {activeTab === "auctions" && profileData && (
          <div className="mt-4">
            {profileData.auctions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profileData.auctions.map((auction, index) => (
                  <div
                    key={index}
                    className="p-4 border rounded-lg shadow-sm hover:shadow-md transition"
                  >
                    <h3 className="font-semibold">{auction.title}</h3>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <p className="text-sm text-gray-500">Start Price</p>
                        <p>
                          {(auction.blockchain_metadata?.price || 0) /
                            1_000_000}{" "}
                          ꜩ
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">End Time</p>
                        <p>{formatDate(auction.auctionDetails?.end_time)}</p>
                      </div>
                    </div>
                    {auction.BidsList && auction.BidsList.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium">Bids:</p>
                        <ul className="text-sm space-y-1 mt-1">
                          {auction.BidsList.map((bid, bidIndex) => (
                            <li key={bidIndex} className="flex justify-between">
                              <span>{bid.bidder}</span>
                              <span>{bid.amount / 1_000_000} ꜩ</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No active auctions
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
